import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
    animationPhasesUseScrollWindows,
    assertConsistentScrollWindows,
    getLayoutAtScrollWindows
} from "./scroll-window-phases";
import {
    ANIMATION_PHASES_SCROLL_TIMELINE_UNITS_DEFAULT,
    type AnimationPhase,
    type AnimationPhasesDrive,
    type WebNodeConfig
} from "./spiderweb-types";
import { getNodeLayoutDimensions, type NodeCenterPct } from "./spiderweb-utils";

function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v));
}

function clampMinSize(v: number): number {
    return Math.max(1, v);
}

function dimsForSize(node: WebNodeConfig, sz: number) {
    return getNodeLayoutDimensions({ ...node, size: sz });
}

// eslint-disable-next-line complexity
export function getLayoutAtPhaseTime(
    node: WebNodeConfig,
    baseX: number,
    baseY: number,
    baseOpacity: number,
    phases: AnimationPhase[] | undefined,
    t: number
): NodeCenterPct {
    const o0 = clamp01(baseOpacity);

    if (!phases?.length) {
        return { x: baseX, y: baseY, opacity: o0 };
    }

    const bases = getNodeLayoutDimensions(node);
    let x = baseX;
    let y = baseY;
    let opacity = o0;
    let sz = bases.size;
    let bw = bases.boxWidth;
    let bh = bases.boxHeight;
    let tAcc = 0;

    for (const p of phases) {
        const holdEnd = tAcc + (p?.delay ?? 0);
        if (t < holdEnd) {
            return {
                x,
                y,
                opacity,
                size: sz,
                width: bw,
                height: bh
            };
        }
        tAcc = holdEnd;
        const moveEnd = tAcc + (p?.duration ?? 0);
        const opacityStart = opacity;
        const opacityEnd =
            p.opacity !== undefined ? clamp01(p.opacity) : opacity;

        const szStart = sz;
        const bwStart = bw;
        const bhStart = bh;
        const szEnd = p.size !== undefined ? clampMinSize(p.size) : szStart;
        const bwEnd =
            p.width !== undefined
                ? p.width
                : p.size !== undefined
                ? dimsForSize(node, szEnd).boxWidth
                : bwStart;
        const bhEnd =
            p.height !== undefined
                ? p.height
                : p.size !== undefined
                ? dimsForSize(node, szEnd).boxHeight
                : bhStart;

        if (t < moveEnd) {
            const u =
                (p?.duration ?? 0) > 0 ? (t - tAcc) / (p?.duration ?? 0) : 1;
            const e = easeInOutCubic(Math.min(1, Math.max(0, u)));
            const szMid = szStart + (szEnd - szStart) * e;
            const opacityMid = opacityStart + (opacityEnd - opacityStart) * e;
            const xEnd = p.x !== undefined ? p.x : x;
            const yEnd = p.y !== undefined ? p.y : y;

            let bwMid: number;
            if (p.width !== undefined) {
                bwMid = bwStart + (bwEnd - bwStart) * e;
            } else if (p.size !== undefined) {
                bwMid = dimsForSize(node, szMid).boxWidth;
            } else {
                bwMid = bwStart;
            }

            let bhMid: number;
            if (p.height !== undefined) {
                bhMid = bhStart + (bhEnd - bhStart) * e;
            } else if (p.size !== undefined) {
                bhMid = dimsForSize(node, szMid).boxHeight;
            } else {
                bhMid = bhStart;
            }

            return {
                x: x + (xEnd - x) * e,
                y: y + (yEnd - y) * e,
                opacity: opacityMid,
                size: szMid,
                width: bwMid,
                height: bhMid
            };
        }
        tAcc = moveEnd;
        x = p.x !== undefined ? p.x : x;
        y = p.y !== undefined ? p.y : y;
        opacity = opacityEnd;
        sz = szEnd;
        bw = bwEnd;
        bh = bhEnd;
    }

    return {
        x,
        y,
        opacity,
        size: sz,
        width: bw,
        height: bh
    };
}

export function getTotalPhaseDurationSec(
    phases: AnimationPhase[] | undefined
): number {
    if (!phases?.length) return 0;
    if (animationPhasesUseScrollWindows(phases)) {
        return Math.max(0, ...phases.map((p) => p.scrollWindowEnd ?? 0));
    }
    let total = 0;
    for (const p of phases) {
        total += p.delay + p.duration;
    }
    return total;
}

export function anyNodeHasPhases(nodes: WebNodeConfig[]): boolean {
    return nodes.some((n) => n.animationPhases?.length);
}

export type NodePositionPct = NodeCenterPct;

export type SpiderwebPhasePositionsOptions = {
    drive?: AnimationPhasesDrive;
    scrollProgress?: number;
};

export function useSpiderwebPhasePositions(
    nodes: WebNodeConfig[],
    active: boolean,
    phaseClockStartOffsetSec: number,
    animationsPaused: boolean,
    options?: SpiderwebPhasePositionsOptions
): ReadonlyMap<string, NodePositionPct> {
    const drive: AnimationPhasesDrive = options?.drive ?? "time";
    const scrollProgress = options?.scrollProgress ?? 0;
    const baseMap = useMemo(() => {
        const m = new Map<string, NodePositionPct>();
        for (const n of nodes) {
            m.set(n.id, {
                x: n.x,
                y: n.y,
                opacity: clamp01(n.opacity ?? 1)
            });
        }
        return m;
    }, [nodes]);

    const hasPhases = useMemo(() => anyNodeHasPhases(nodes), [nodes]);

    const maxDurationSec = useMemo(() => {
        let max = 0;
        for (const n of nodes) {
            max = Math.max(max, getTotalPhaseDurationSec(n.animationPhases));
        }
        return max;
    }, [nodes]);

    const [liveMap, setLiveMap] = useState(() => baseMap);

    useEffect(() => {
        setLiveMap(new Map(baseMap));
    }, [baseMap]);

    const pausedRef = useRef(animationsPaused);
    pausedRef.current = animationsPaused;

    const frozenScrollProgressRef = useRef(0);
    const wasAnimationPausedRef = useRef(false);
    const [scrollPauseEpoch, setScrollPauseEpoch] = useState(0);

    useLayoutEffect(() => {
        if (drive !== "scroll") {
            return;
        }
        if (animationsPaused && !wasAnimationPausedRef.current) {
            frozenScrollProgressRef.current = scrollProgress;
            setScrollPauseEpoch((e) => e + 1);
        }
        wasAnimationPausedRef.current = animationsPaused;
    }, [drive, animationsPaused, scrollProgress]);

    const effectiveScrollProgress =
        drive === "scroll" && animationsPaused
            ? frozenScrollProgressRef.current
            : scrollProgress;

    const scrollDerivedMap = useMemo(() => {
        if (drive !== "scroll" || !active || !hasPhases) {
            return baseMap;
        }
        const m = new Map<string, NodePositionPct>();
        const scrollPercent100 = effectiveScrollProgress * 100;
        for (const n of nodes) {
            assertConsistentScrollWindows(n.id, n.animationPhases);
            const phases = n.animationPhases;
            if (animationPhasesUseScrollWindows(phases)) {
                m.set(
                    n.id,
                    getLayoutAtScrollWindows(
                        n,
                        n.x,
                        n.y,
                        n.opacity ?? 1,
                        (phases as AnimationPhase[]) || [],
                        scrollPercent100
                    )
                );
            } else {
                const t =
                    effectiveScrollProgress *
                    ANIMATION_PHASES_SCROLL_TIMELINE_UNITS_DEFAULT;
                m.set(
                    n.id,
                    getLayoutAtPhaseTime(n, n.x, n.y, n.opacity ?? 1, phases, t)
                );
            }
        }
        return m;
    }, [
        drive,
        active,
        hasPhases,
        nodes,
        baseMap,
        effectiveScrollProgress,
        scrollProgress,
        animationsPaused,
        scrollPauseEpoch
    ]);

    const timelineStartMsRef = useRef<number | null>(null);
    const pauseClockRef = useRef({
        totalPausedMs: 0,
        segmentStartMs: null as number | null
    });
    const lastNodesKeyRef = useRef<string>("");

    useEffect(() => {
        if (drive !== "time" || !active || !hasPhases) {
            return;
        }

        const applyLayouts = (elapsedSec: number) => {
            const m = new Map<string, NodePositionPct>();
            const scrollPctFromTime =
                maxDurationSec > 0
                    ? Math.min(100, (elapsedSec / maxDurationSec) * 100)
                    : 0;
            for (const n of nodes) {
                const phases = n.animationPhases;
                const layout =
                    animationPhasesUseScrollWindows(phases) && phases?.length
                        ? getLayoutAtScrollWindows(
                              n,
                              n.x,
                              n.y,
                              n.opacity ?? 1,
                              phases,
                              scrollPctFromTime
                          )
                        : getLayoutAtPhaseTime(
                              n,
                              n.x,
                              n.y,
                              n.opacity ?? 1,
                              phases,
                              elapsedSec
                          );
                m.set(n.id, layout);
            }
            setLiveMap(m);
        };

        if (maxDurationSec <= 0) {
            applyLayouts(0);
            return;
        }

        const nodesKey = nodes.map((n) => n.id).join("|");
        if (lastNodesKeyRef.current !== nodesKey) {
            lastNodesKeyRef.current = nodesKey;
            timelineStartMsRef.current = null;
            pauseClockRef.current = {
                totalPausedMs: 0,
                segmentStartMs: null
            };
        }

        if (timelineStartMsRef.current === null) {
            timelineStartMsRef.current = performance.now();
        }
        const start = timelineStartMsRef.current;

        let frame = 0;
        const offset = Math.max(0, phaseClockStartOffsetSec);

        const tick = () => {
            const pc = pauseClockRef.current;
            if (pausedRef.current) {
                if (pc.segmentStartMs === null) {
                    pc.segmentStartMs = performance.now();
                }
            } else if (pc.segmentStartMs !== null) {
                pc.totalPausedMs += performance.now() - pc.segmentStartMs;
                pc.segmentStartMs = null;
            }

            let extraPauseMs = pc.totalPausedMs;
            if (pausedRef.current && pc.segmentStartMs !== null) {
                extraPauseMs += performance.now() - pc.segmentStartMs;
            }

            const wallSec = (performance.now() - start - extraPauseMs) / 1000;
            const phaseSec = Math.max(0, wallSec - offset);
            applyLayouts(phaseSec);

            const done = phaseSec >= maxDurationSec + 0.016;
            if (done) {
                return;
            }
            if (pausedRef.current) {
                return;
            }
            frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [
        drive,
        active,
        hasPhases,
        maxDurationSec,
        nodes,
        phaseClockStartOffsetSec,
        animationsPaused
    ]);

    if (!active || !hasPhases) {
        return baseMap;
    }

    if (drive === "scroll") {
        return scrollDerivedMap;
    }

    return liveMap;
}
